import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    IconCoins,
    IconDiscount,
    IconUserCog,
    IconLanguage,
    IconWorldCog,
    IconRoute2,
    IconChevronRight,
    IconLock,
    IconReceiptTax,
    IconBell,
    IconMail,
    IconBrandGoogle
} from '@tabler/icons-react';
import PageHeader from '@/components/page-header';
import { setWebTitle } from '@/lib/format-utils';
import {
    SETTINGS_CHANGE_PASSWORD_PATH,
    SETTINGS_EXCHANGE_RATES_PATH,
    SETTINGS_DISCOUNT_CATEGORIES_PATH,
    SETTINGS_USERS_PATH,
    SETTINGS_LANGUAGE_PATH,
    SETTINGS_ONLINE_STORE_PATH,
    SETTINGS_SERVICE_ORDERS_PATH,
    SETTINGS_TAX_SETTINGS_PATH,
    SETTINGS_NOTIFICATIONS_PATH,
    SETTINGS_EMAILS_PATH,
    SETTINGS_GMAIL_PATH,
} from '@/lib/navigation';

export default function TenantSettingsPage(): JSX.Element {
    const { t } = useTranslation();
    setWebTitle(t('settings'));

    const settingsItems = [
        {
            path: SETTINGS_USERS_PATH,
            label: t('users'),
            description: t('users_settings_desc'),
            icon: <IconUserCog size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_ONLINE_STORE_PATH,
            label: t('online_store'),
            description: t('online_store_settings_desc'),
            icon: <IconWorldCog size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_LANGUAGE_PATH,
            label: t('language'),
            description: t('language_settings_desc'),
            icon: <IconLanguage size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_EXCHANGE_RATES_PATH,
            label: t('exchange_rates'),
            description: t('exchange_rates_settings_desc'),
            icon: <IconCoins size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_DISCOUNT_CATEGORIES_PATH,
            label: t('discounts'),
            description: t('discounts_settings_desc'),
            icon: <IconDiscount size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_SERVICE_ORDERS_PATH,
            label: t('service_orders'),
            description: t('service_orders_settings_desc'),
            icon: <IconRoute2 size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_TAX_SETTINGS_PATH + '?c=Argentina',
            label: t('AFIP'),
            description: t('arg_tax_settings_desc'),
            icon: <IconReceiptTax size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_NOTIFICATIONS_PATH,
            label: t('notifications_settings'),
            description: t('notifications_settings_desc'),
            icon: <IconBell size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_EMAILS_PATH,
            label: t('emails_settings'),
            description: t('emails_settings_desc'),
            icon: <IconMail size={28} stroke={1.5} color="black" />,
        },
        {
            path: SETTINGS_GMAIL_PATH,
            label: t('gmail_link'),
            description: t('gmail_link_desc'),
            icon: <IconBrandGoogle size={28} stroke={1.5} color="black" />,
        },
        // {
        //     path: SETTINGS_CHANGE_PASSWORD_PATH,
        //     label: t('change_password'),
        //     description: t('change_password_description'),
        //     icon: <IconLock size={28} stroke={1.5} color="black" />,
        // },
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <PageHeader title={t('settings')} />

            <div className="mt-4 mb-8">
                <h1 className="text-3xl font-semibold text-gray-900 tracking-tight mb-2">
                    {t('settings')}
                </h1>
                <p className="text-gray-500 text-sm">
                    {t('settings_subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {settingsItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className="solid-box-border-hover flex gap-2 items-center"
                    >
                        <div className="p-4">
                            {item.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-base mb-1 group-hover:text-blue-600 transition-colors duration-150">
                                {item.label}
                            </h3>
                            <p className="text-gray-500 text-xs leading-relaxed">
                                {item.description}
                            </p>
                        </div>
                        <IconChevronRight
                            size={18}
                            stroke={1.5}
                            className="text-gray-400 mt-2 transition-transform duration-200 group-hover:translate-x-1"
                        />
                    </Link>
                ))}
            </div>
        </div>
    );
}
